/** Per fetch attempt, not a whole-query budget: transient failures may still retry. */
export const SUPABASE_FETCH_DEADLINE_MS = 8000

/** Safari versions supporting timeout() may still lack any(). */
function combineSignals(caller: AbortSignal, deadline: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([caller, deadline])
  const controller = new AbortController()
  const cleanup = () => {
    caller.removeEventListener('abort', onCallerAbort)
    deadline.removeEventListener('abort', onDeadlineAbort)
  }
  const abort = (reason: unknown) => {
    cleanup()
    controller.abort(reason)
  }
  const onCallerAbort = () => abort(caller.reason)
  const onDeadlineAbort = () => abort(deadline.reason)
  if (caller.aborted) onCallerAbort()
  else if (deadline.aborted) onDeadlineAbort()
  else {
    caller.addEventListener('abort', onCallerAbort, { once: true })
    deadline.addEventListener('abort', onDeadlineAbort, { once: true })
  }
  return controller.signal
}

/**
 * Composed signals retain cancellation through response-body consumption.
 * Keep cancellation past headers; fallback listeners are removed on cancellation
 * or the deadline, including when the response finishes before that deadline.
 * PostgREST retries TimeoutError, but recognizes AbortError as terminal cancellation.
 */
export const fetchWithSupabaseDeadline: typeof fetch = async (input, init) => {
  const requestSignal = typeof Request !== 'undefined' && input instanceof Request ? input.signal : undefined
  // Fetch semantics: undefined inherits Request.signal; null explicitly clears it.
  const callerSignal = init?.signal === undefined ? requestSignal : init.signal
  if (callerSignal?.aborted) throw new DOMException('Supabase request cancelled', 'AbortError')
  const deadline = AbortSignal.timeout(SUPABASE_FETCH_DEADLINE_MS)
  const signal = callerSignal ? combineSignals(callerSignal, deadline) : deadline
  try {
    return await fetch(input, { ...init, signal })
  } catch (error) {
    if (signal.aborted) {
      throw new DOMException(deadline.aborted ? 'Supabase fetch deadline exceeded' : 'Supabase request cancelled', 'AbortError')
    }
    throw error
  }
}
